import { useEffect, useState } from "react";

import { Link } from "react-router-dom";

import { Container } from "@mui/material";

import {
  ActionIcon,
  Autocomplete,
  Button,
  CopyButton,
  Menu,
  Modal,
  Pagination,
  Select,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDots,
  IconDownload,
  IconEdit,
  IconExternalLink,
  IconFilter,
  IconPlus,
  IconSearch,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";

import { CSVLink } from "react-csv";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";

import { CarsAndModels, Colors, years } from "../../lib/Data";
import { Endpoints } from "../../lib/Endpoints";
import { NonPaginatedResponse, PaginatedResponse } from "../../lib/Responses";
import { VehicleProps } from "../../lib/Types";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";

import AnimateOnLoad from "../../components/AnimateOnLoad";
import DatagridTable from "../../components/DatagridTable";
import VehicleForm from "../../components/VehicleForm";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getColorFromVehicleStatus,
  getPaginationTotal,
  isStringEmpty,
  removeNullUndefinedFields,
  validateVIN,
} from "../../lib/Methods";
import "./styles.scss";

const Inventory: React.FC = () => {
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });

  const filterForm = useForm({
    initialValues: {
      status: "all",
      model: "",
      manufacturer: "",
      year: "",
    },
    validate: {
      status: (value) => (value && value.length > 2 ? null : "Select a status"),
    },
  });

  const getVehiclesFilter = () => {
    const { model, manufacturer, year, status } = filterForm.values;
    const filter = removeNullUndefinedFields({
      model: isStringEmpty(model) ? undefined : model,
      manufacturer: isStringEmpty(manufacturer) ? undefined : manufacturer,
      year: isStringEmpty(year) ? undefined : year,
    });
    return {
      ...filter,
      sold: status === "all" ? undefined : status === "sold",
    };
  };

  // const [isUseFilter, setUseFilter] = useState<boolean>(false);

  const {
    data: inventory,
    isLoading: isInventoryLoading,
    reloadData: reloadInventory,
  } = usePerformRequest<PaginatedResponse<VehicleProps[]>>({
    method: "POST",
    route: Endpoints.GetFilterVehiclesInInventory(pagination.page, pagination.limit),

    // body: !isUseFilter ? { sold: undefined } : getVehiclesFilter(),
    body: getVehiclesFilter(),
  });

  const [tableView, setTableView] = useState<"all" | "vin-search">("all");
  useEffect(() => {
    reloadInventory();
  }, [filterForm.values]);
  // useEffect(() => {
  //   reloadInventory();
  // }, [isUseFilter]);

  const [currentActionsMenu, setCurrentActionsMenu] = useState<string>("");

  const [isUpdating, setUpdating] = useState<boolean>(false);
  const UpdateVehicleStatus = async (vehicleID: string, status: boolean) => {
    setUpdating(true);
    const r = await PerformRequest({
      method: "PATCH",
      data: {
        sold: status,
      },
      route: Endpoints.GetUpdateVehicleStatus(vehicleID),
      callback: () => {
        setUpdating(false);
      },
    });
    setUpdating(false);
    if (r) {
      if (r.status === 200) {
        setCurrentActionsMenu("");
        DefaultSuccessNotification("Vehicle has been updated");
        reloadInventory();
      } else {
        DefaultErrorNotification((r?.data as any)[0]);
      }
    }
  };

  const [currentVehicle, setCurrentVehicle] = useState<VehicleProps>();
  const [vehicleFormAction, setVehicleFormAction] = useState<"add" | "update">("add");

  const [isVehicleModalOpen, { open: OpenVehicleModal, close: CloseVehicleModal }] =
    useDisclosure(false);

  const [isSearchModalOpen, { open: OpenSearchModal, close: CloseSearchModal }] =
    useDisclosure(false);

  const [
    isDeleteModalOpen,
    { open: OpenDeleteVehicleModal, close: CloseDeleteVehicleModal },
  ] = useDisclosure(false);

  const DeleteVehicle = async () => {
    setUpdating(true);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetUpdateVehicleStatus(currentVehicle?.id as string),
      callback: () => {
        setUpdating(false);
      },
    });
    setUpdating(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Vehicle has been deleted");
        CloseDeleteVehicleModal();
        setCurrentVehicle(undefined);
        reloadInventory();
      } else {
        DefaultErrorNotification((r?.data as any)[0]);
      }
    }
  };

  const vinForm = useForm({
    initialValues: {
      vin: "",
    },
    validate: {
      vin: (value) => (validateVIN(value) ? null : "Enter a valid VIN"),
    },
  });

  const [isVehicleLoading, setVehicleLoading] = useState<boolean>(false);
  const [vehicleSearchResults, setVehicleSearchResult] = useState<VehicleProps[]>([]);
  const FindVehicleUsingVIN = async () => {
    const { hasErrors } = vinForm.validate();
    if (!hasErrors) {
      setVehicleLoading(true);
      const r = await PerformRequest<NonPaginatedResponse<VehicleProps[]>>({
        method: "GET",
        route: Endpoints.GetSingleVehicleWithVIN(vinForm.values.vin),

        callback: () => {
          setVehicleLoading(false);
        },
      });
      setVehicleLoading(false);
      if (r) {
        if (r.status === 404) {
          DefaultErrorNotification("Vehicle does not exist!");
        } else if (r.status === 200) {
          DefaultSuccessNotification("Vehicle found!");
          setVehicleSearchResult(r?.data?.data);
          CloseSearchModal();
          setTableView("vin-search");
        }
      } else {
        DefaultErrorNotification("Could not get vehicle details");
      }
    }
  };
  const getInputProps = (id: string) => {
    return {
      label: id,
      w: 120,
      mr: 5,
      style: {
        textTransform: "capitalize",
      },
      spellCheck: false,
    } as any;
  };
  return (
    <AnimateOnLoad>
      <Container>
        <div className="inventory-container">
          <div className="flex-row align-center justify-between width-100">
            <div className="flex-col">
              <Text className="component-header">Vehicle Inventory</Text>
              <div className="flex-col">
                <div className="flex-row align-center">
                  <Select
                    allowDeselect={false}
                    {...filterForm.getInputProps("status")}
                    {...getInputProps("status")}
                    styles={{
                      option: {
                        textTransform: "capitalize",
                      },
                      input: {
                        textTransform: "capitalize",
                      },
                      label: {
                        textTransform: "capitalize",
                      },
                    }}
                    data={[
                      { label: "All", value: "all" },
                      { label: "Sold", value: "sold" },
                      { label: "Available", value: "unsold" },
                    ]}
                  />
                  <Autocomplete
                    clearable
                    rightSection={<IconSelector size={15} />}
                    searchable
                    {...filterForm.getInputProps("manufacturer")}
                    {...getInputProps("manufacturer")}
                    allowDeselect={false}
                    placeholder="E.g Honda"
                    data={CarsAndModels.map((c) => {
                      return {
                        label: c.brand === "" ? "All" : c.brand,
                        value: c.brand.toLowerCase(),
                      };
                    })}
                  />
                  <Autocomplete
                    clearable
                    rightSection={<IconSelector size={15} />}
                    searchable
                    data={years.map((year) => {
                      return {
                        label: year,
                        value: year,
                      };
                    })}
                    {...filterForm.getInputProps("year")}
                    {...getInputProps("year")}
                    placeholder="E.g 2001"
                  />
                  <Autocomplete
                    clearable
                    rightSection={<IconSelector size={15} />}
                    searchable
                    data={CarsAndModels.find(
                      (c) => c.brand === filterForm.values.manufacturer
                    )?.models?.map((model) => {
                      return {
                        label: model,
                        value: model.toLowerCase(),
                      };
                    })}
                    {...filterForm.getInputProps("model")}
                    {...getInputProps("model")}
                  />
                </div>
                {/* <div className="flex-row">
                  <Button
                    w={100}
                    mt={15}
                    color={Colors.BluePrimary}
                    radius="50px"
                    variant="outline"
                    disabled={isInventoryLoading || isVehicleLoading}
                    onClick={() => {
                      setUseFilter(filterForm.isDirty());
                      setTableView("all");
                      if (!isUseFilter) {
                        reloadInventory();
                      }
                    }}
                  >
                    {filterForm.isDirty() ? "Filter" : "Reload"}
                  </Button>
                  &nbsp; &nbsp;
                  <Button
                    w={100}
                    mt={15}
                    color={Colors.RedPrimary}
                    radius="50px"
                    variant="outline"
                    leftSection={<IconX size={18} />}
                    disabled={isInventoryLoading || isVehicleLoading}
                    onClick={() => {
                      setUseFilter(false);
                      filterForm.reset();
                      setTableView("all");
                    }}
                  >
                    Clear
                  </Button>
                </div> */}
              </div>
            </div>
            <div className="flex-row align-center">
              <Button
                mr={10}
                color={Colors.BluePrimary}
                style={{ borderRadius: "50px" }}
                disabled={isInventoryLoading || isVehicleLoading}
                onClick={() => {
                  OpenSearchModal();
                }}
                leftSection={<IconSearch size={16} />}
              >
                Find
              </Button>
              <Button
                color={Colors.GreenPrimary}
                style={{ borderRadius: "50px" }}
                disabled={isInventoryLoading || isVehicleLoading}
                onClick={() => {
                  OpenVehicleModal();
                  setVehicleFormAction("add");
                  setCurrentVehicle(undefined);
                }}
                leftSection={<IconPlus size={16} />}
              >
                Add
              </Button>
              <CSVLink
                data={inventory?.data ?? []}
                headers={[
                  { label: "Manufacturer", key: "manufacturer" },
                  { label: "Model", key: "model" },
                  { label: "Year", key: "year" },
                  { label: "Amount", key: "amount" },
                  { label: "Vin", key: "vin" },
                  { label: "Sold", key: "sold" },
                  { label: "Color", key: "color" },
                ]}
              >
                <Button
                  ml={10}
                  color={Colors.BluePrimary}
                  variant="outline"
                  style={{ borderRadius: "50px" }}
                  disabled={
                    isInventoryLoading ||
                    !inventory ||
                    !inventory.data ||
                    inventory.data.length === 0
                  }
                  leftSection={<IconDownload size={16} />}
                >
                  Export
                </Button>
              </CSVLink>
            </div>
          </div>
          <br />
          <br />

          <>
            {tableView === "all" ? (
              <>
                {inventory && inventory.data && inventory.data.length > 0 ? (
                  <DatagridTable
                    isDataLoading={isInventoryLoading}
                    rows={inventory.data.map((vehicle) => {
                      return vehicle;
                    })}
                    columns={[
                      {
                        flex: 1,
                        field: "manufacturer",
                        headerName: "Manufacturer",
                        width: 130,
                      },
                      { flex: 1, field: "model", headerName: "Model", width: 130 },
                      { flex: 1, field: "year", headerName: "Year", width: 90 },
                      {
                        flex: 1,
                        field: "vin",
                        headerName: "VIN",
                        width: 150,
                        renderCell: (params) => {
                          return (
                            <CopyButton value={params.row.vin}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={copied ? "Copied to clipboard" : "Copy VIN"}
                                >
                                  <span
                                    onClick={copy}
                                    style={{
                                      cursor: "pointer",
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                    }}
                                  >
                                    {params.row.vin}
                                  </span>
                                </Tooltip>
                              )}
                            </CopyButton>
                          );
                        },
                      },
                      {
                        flex: 1,
                        field: "status",
                        headerName: "Status",
                        width: 110,
                        renderCell: (params) => {
                          return (
                            <span
                              style={{
                                color: getColorFromVehicleStatus(params.row.sold),
                                textTransform: "capitalize",
                              }}
                            >
                              {params.row.sold ? "Sold" : "Still available"}
                            </span>
                          );
                        },
                      },

                      {
                        flex: 1,
                        field: "id",
                        headerName: "Actions",
                        renderCell: (params) => {
                          return (
                            <Menu
                              shadow="md"
                              opened={currentActionsMenu === params.row.id}
                              width={200}
                              trigger="hover"
                            >
                              <Menu.Target>
                                <ActionIcon
                                  onClick={() => {
                                    setCurrentActionsMenu(
                                      currentActionsMenu === params.row.id
                                        ? ""
                                        : params.row.id
                                    );
                                  }}
                                  variant="subtle"
                                >
                                  <IconDots size={15.5} />
                                </ActionIcon>
                              </Menu.Target>

                              <Menu.Dropdown>
                                <Menu.Label>Actions</Menu.Label>
                                <Menu.Item
                                  leftSection={<IconFilter size={14} />}
                                  disabled={isInventoryLoading || isVehicleLoading}
                                >
                                  <Menu shadow="md" width={140}>
                                    <Menu.Target>
                                      <Text fz={14}>Mark as</Text>
                                    </Menu.Target>
                                    <Menu.Dropdown>
                                      <div className="flex-col">
                                        {[true, false].map((status) => {
                                          return (
                                            <Button
                                              disabled={
                                                isInventoryLoading || isVehicleLoading
                                              }
                                              className="table-status-btn"
                                              tt="capitalize"
                                              variant="subtle"
                                              c={getColorFromVehicleStatus(status)}
                                              mb={4}
                                              loading={isUpdating}
                                              onClick={() => {
                                                UpdateVehicleStatus(
                                                  params.row.id,
                                                  status
                                                );
                                              }}
                                            >
                                              {status ? "Sold" : "Still available"}
                                            </Button>
                                          );
                                        })}
                                      </div>
                                    </Menu.Dropdown>
                                  </Menu>
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconTrash size={14} />}
                                  disabled={isInventoryLoading || isVehicleLoading}
                                  color={Colors.RedPrimary}
                                  onClick={() => {
                                    OpenDeleteVehicleModal();
                                    setCurrentActionsMenu("");
                                    setCurrentVehicle(params.row);
                                  }}
                                >
                                  Delete
                                </Menu.Item>

                                <Menu.Item
                                  onClick={() => {
                                    setCurrentVehicle(params.row);
                                    setCurrentActionsMenu("");
                                    OpenVehicleModal();
                                    setVehicleFormAction("update");
                                  }}
                                  leftSection={<IconEdit size={14} />}
                                >
                                  Update Details
                                </Menu.Item>
                                <Link to={`/dashboard/inventory/${params.row.id}`}>
                                  <Menu.Item
                                    c={Colors.BluePrimary}
                                    leftSection={<IconExternalLink size={14} />}
                                  >
                                    View
                                  </Menu.Item>
                                </Link>
                              </Menu.Dropdown>
                            </Menu>
                          );
                        },
                      },
                    ]}
                  />
                ) : (
                  <>
                    {isInventoryLoading ? (
                      <MegaLoader />
                    ) : (
                      <DisplayAlert
                        title="No Vehicles Found"
                        children={
                          <Text>No vehicles in the inventory match this criteria!</Text>
                        }
                      />
                    )}
                  </>
                )}
              </>
            ) : (
              <TabularData
                headers={["Manufacturer", "Year", "Model", "Vin", "Status", "Actions"]}
              >
                <Table.Tbody>
                  {vehicleSearchResults?.map((vehicle) => {
                    const {
                      id: vehicleID,
                      manufacturer,
                      model,
                      year,
                      vin,
                      sold,
                    } = vehicle;
                    return (
                      <Table.Tr key={vehicleID}>
                        <Table.Td tt="capitalize">{manufacturer}</Table.Td>
                        <Table.Td tt="capitalize">{year}</Table.Td>
                        <Table.Td tt="capitalize">{model}</Table.Td>
                        <CopyButton value={vin}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Copied to clipboard" : "Copy VIN"}>
                              <Table.Td
                                onClick={copy}
                                style={{ cursor: "pointer" }}
                                c={copied ? Colors.GreenPrimary : Colors.BluePrimary}
                              >
                                {vin}
                              </Table.Td>
                            </Tooltip>
                          )}
                        </CopyButton>
                        <Table.Td c={getColorFromVehicleStatus(sold)}>
                          {sold ? "Sold" : "Still available"}
                        </Table.Td>

                        <Table.Td>
                          <Menu
                            shadow="md"
                            opened={currentActionsMenu === vehicle.id}
                            width={200}
                            trigger="hover"
                          >
                            <Menu.Target>
                              <ActionIcon
                                onClick={() => {
                                  setCurrentActionsMenu(
                                    currentActionsMenu === vehicle.id ? "" : vehicle.id
                                  );
                                }}
                                variant="subtle"
                              >
                                <IconDots size={15.5} />
                              </ActionIcon>
                            </Menu.Target>

                            <Menu.Dropdown>
                              <Menu.Label>Actions</Menu.Label>
                              <Menu.Item
                                leftSection={<IconFilter size={14} />}
                                disabled={isInventoryLoading || isVehicleLoading}
                              >
                                <Menu shadow="md" width={140}>
                                  <Menu.Target>
                                    <Text fz={14}>Mark as</Text>
                                  </Menu.Target>
                                  <Menu.Dropdown>
                                    <div className="flex-col">
                                      {[true, false].map((status) => {
                                        return (
                                          <Button
                                            disabled={
                                              isInventoryLoading || isVehicleLoading
                                            }
                                            className="table-status-btn"
                                            tt="capitalize"
                                            variant="subtle"
                                            c={getColorFromVehicleStatus(status)}
                                            mb={4}
                                            onClick={() => {
                                              UpdateVehicleStatus(vehicleID, status);
                                            }}
                                          >
                                            {status ? "Sold" : "Still available"}
                                          </Button>
                                        );
                                      })}
                                    </div>
                                  </Menu.Dropdown>
                                </Menu>
                              </Menu.Item>
                              <Menu.Item
                                leftSection={<IconTrash size={14} />}
                                disabled={isInventoryLoading || isVehicleLoading}
                                color={Colors.RedPrimary}
                                onClick={() => {
                                  OpenDeleteVehicleModal();
                                  setCurrentActionsMenu("");
                                  setCurrentVehicle(vehicle);
                                }}
                              >
                                Delete
                              </Menu.Item>

                              <Menu.Item
                                onClick={() => {
                                  setCurrentVehicle(vehicle);
                                  setCurrentActionsMenu("");
                                  OpenVehicleModal();
                                  setVehicleFormAction("update");
                                }}
                                leftSection={<IconEdit size={14} />}
                              >
                                Update Details
                              </Menu.Item>
                              <Link to={`/dashboard/inventory/${vehicle.id}`}>
                                <Menu.Item
                                  c={Colors.BluePrimary}
                                  leftSection={<IconExternalLink size={14} />}
                                >
                                  View
                                </Menu.Item>
                              </Link>
                            </Menu.Dropdown>
                          </Menu>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })}
                </Table.Tbody>
              </TabularData>
            )}
          </>

          <div className="flex-row align-center justify-center width-100">
            <Pagination
              value={pagination.page}
              onChange={(value) => {
                setPagination({ ...pagination, page: value });
              }}
              my={40}
              disabled={
                inventory?.data.length === 0 || isInventoryLoading || isVehicleLoading
              }
              total={getPaginationTotal(inventory?.totalRecords, pagination.limit)}
            />
          </div>
        </div>

        <Modal
          opened={isDeleteModalOpen}
          onClose={() => {
            CloseDeleteVehicleModal();
          }}
          centered
          title="Delete Vehicle"
        >
          <Text c={Colors.BluePrimary} fz={22} fw={500}>
            {currentVehicle?.year} {currentVehicle?.manufacturer} {currentVehicle?.model}
          </Text>
          <Text>Are you sure you want to delete this vehicle</Text>
          <Text c={Colors.RedPrimary}>
            This action&nbsp;
            <b>CANNOT</b>&nbsp;be undone
          </Text>
          <div className="flex-row align-center">
            <Button
              loading={isUpdating || isVehicleLoading}
              onClick={DeleteVehicle}
              mt={10}
              radius="50px"
              color={Colors.RedPrimary}
            >
              Delete
            </Button>
            &nbsp; &nbsp;
            <Button
              mt={10}
              w={120}
              radius="50px"
              color={Colors.GreenPrimary}
              variant="outline"
              onClick={CloseDeleteVehicleModal}
            >
              Cancel
            </Button>
          </div>
        </Modal>
        <Modal
          opened={isSearchModalOpen}
          onClose={() => {
            CloseSearchModal();
          }}
          centered
          title="Find Vehicle with VIN"
        >
          <TextInput
            label="VIN"
            spellCheck={false}
            disabled={isVehicleLoading}
            {...vinForm.getInputProps("vin")}
            placeholder="Enter Vehicle VIN"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                FindVehicleUsingVIN();
              }
            }}
          />
          <div className="flex-row align-center">
            <Button
              onClick={FindVehicleUsingVIN}
              mt={10}
              w={120}
              loading={isVehicleLoading}
              radius="50px"
              color={Colors.GreenPrimary}
            >
              Find
            </Button>
            &nbsp; &nbsp;
            <Button
              mt={10}
              w={120}
              radius="50px"
              color={Colors.RedPrimary}
              variant="outline"
              onClick={() => {
                CloseSearchModal();
                vinForm.reset();
              }}
            >
              Cancel
            </Button>
          </div>
        </Modal>

        <VehicleForm
          useCreateRoute={vehicleFormAction === "add"}
          action={vehicleFormAction}
          isFormOpen={isVehicleModalOpen}
          CloseForm={CloseVehicleModal}
          OpenForm={OpenVehicleModal}
          OnComplete={() => {
            reloadInventory();
          }}
          shouldShowImages={vehicleFormAction === "add"}
          // @ts-ignore
          vehicle={
            vehicleFormAction === "add"
              ? undefined
              : {
                  id: currentVehicle?.id,
                  previousOwner: {
                    email: currentVehicle?.previousOwner?.email,
                    phone: currentVehicle?.previousOwner?.phone,
                    name: currentVehicle?.previousOwner?.name,
                  },
                  manufacturer: currentVehicle?.manufacturer,
                  createdAt: currentVehicle?.createdAt,
                  odometer: currentVehicle?.odometer,
                  amount: currentVehicle?.amount,
                  images: currentVehicle?.images,
                  purchaseDate: currentVehicle?.purchaseDate,
                  notes: currentVehicle?.notes,
                  model: currentVehicle?.model,
                  year: currentVehicle?.year,
                  color: currentVehicle?.color,
                  driveable: currentVehicle?.driveable,
                  vin: currentVehicle?.vin,
                }
          }
        />
      </Container>
    </AnimateOnLoad>
  );
};
export default Inventory;
